/**
 * @fileoverview Hook de Data Layer para gestión de tópicos y sub-tópicos en ConversationTopicsModalMC.
 * Permite cargar tópicos, sub-tópicos y manejar la selección y estado de envío en el modal.
 * @module useConversationTopicsModalData
 * @category Hook - Data Layer
 * @since 3.1.0
 * @version 3.1.1
 */
/**
 * @typedef {Object} TopicOption
 * @description Opción de tópico para el modal.
 * @property {string|number} id - ID del tópico
 * @property {string} label - Nombre del tópico
 * @property {Array<SubtopicOption>} subtopics - Lista de sub-tópicos asociados
 */

/**
 * @typedef {Object} SubtopicOption
 * @description Opción de sub-tópico para el modal.
 * @property {string|number} id - ID del sub-tópico
 * @property {string} label - Nombre del sub-tópico
 */

/**
 * @typedef {Object} UseConversationTopicsModalDataReturn
 * @description Objeto retornado por el hook useConversationTopicsModalData.
 * @property {Array<TopicOption>} topicOptions - Opciones de tópicos
 * @property {Array<SubtopicOption>} subtopicOptions - Opciones de sub-tópicos según tópico seleccionado
 * @property {string|number|null} selectedTopic - ID del tópico seleccionado
 * @property {function} setSelectedTopic - Setter para tópico seleccionado
 * @property {string|number|null} selectedSubtopic - ID del sub-tópico seleccionado
 * @property {function} setSelectedSubtopic - Setter para sub-tópico seleccionado
 * @property {boolean} loading - Estado de carga
 * @property {function} setLoading - Setter para loading
 * @property {boolean} hasEnabledSubmit - Si el submit está habilitado
 */

/**
 * Hook Data Layer para cargar tópicos, sub-tópicos y manejar selección en ConversationTopicsModalMC.
 *
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades:
 * - Solo carga de datos y gestión de estados de API
 * - Sin lógica de UI ni operaciones de negocio
 * - Memoización y debug tracking para performance
 *
 * **Arquitectura Optimizada (v3.1.1):**
 * - ✅ Solo carga de datos y estados (tópicos, sub-tópicos, loading)
 * - ✅ useMemo y useEffect para performance
 *
 * @function useConversationTopicsModalData
 * @returns {UseConversationTopicsModalDataReturn} Estados y handlers para tópicos y sub-tópicos
 *
 * @see {@link ../../../../app/javascript/presentation/modules/inbox/components/ConversationTopicsModalMC.js} Uso en modal de tópicos
 * @see {@link ../../../../app/javascript/actions/conversationTopics.js} Acciones de tópicos
 *
 * @since 3.1.0
 * @version 3.1.1 - Documentación y typedefs agregados
 */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTopics } from '../../../../actions/conversationTopics';

/**
 * Hook Data Layer para cargar tópicos, sub-tópicos y manejar selección.
 *
 * @returns {Object} Estados y handlers para tópicos y sub-tópicos
 */
export default function useConversationTopicsModalData() {
  const dispatch = useDispatch();
  const topics = useSelector((state) => state.conversationTopics.list);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchTopics());
  }, [dispatch]);

  const topicOptions = useMemo(() =>
    topics.map((topic) => ({
      id: topic.id,
      value: topic.id,
      label: topic.name,
      subtopics: topic.conversation_subtopics || []
    })), [topics]);

  const subtopicOptions = useMemo(() =>
    topicOptions?.find((topic) => topic.id === selectedTopic)?.subtopics?.map((sub) => ({
      id: sub.id,
      value: sub.id,
      label: sub.name
    })) || [], [topicOptions, selectedTopic]);

  const hasEnabledSubmit = useMemo(() => {
    if (loading) return false;
    if (!selectedTopic) return false;
    const requiresSubtopic = subtopicOptions?.length > 0;
    if (requiresSubtopic && !selectedSubtopic) return false;
    return true;
  }, [selectedTopic, selectedSubtopic, subtopicOptions, loading]);

  return {
    topicOptions,
    subtopicOptions,
    selectedTopic,
    setSelectedTopic,
    selectedSubtopic,
    setSelectedSubtopic,
    loading,
    setLoading,
    hasEnabledSubmit
  };
}
